import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { TextInput } from "@/components/admin/text-input";
import { FileInput } from "@/components/admin/file-input";
import { FileField } from "@/components/admin/file-field";
import { SelectInput } from "@/components/admin/select-input";
import { DateTimeInput } from "@/components/admin/date-time-input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { Status } from "../misc/Status";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { getCurrentDate } from "./utils";
import { InteractionIcon } from "./InteractionIcon";
import type { InteractionType } from "../types";

const interactionTypeChoices = [
  { id: "note", name: "Note", value: "note" },
  { id: "call", name: "Phone Call", value: "call" },
  { id: "sms", name: "SMS Message", value: "sms" },
  { id: "chat", name: "Chat", value: "chat" },
  { id: "email", name: "Email", value: "email" },
];

const directionChoices = [
  { id: "inbound", name: "Inbound", value: "inbound" },
  { id: "outbound", name: "Outbound", value: "outbound" },
];

export const NoteInputs = ({ showStatus }: { showStatus?: boolean }) => {
  const { noteStatuses } = useConfigurationContext();
  const { setValue, watch } = useFormContext();
  const [displayMore, setDisplayMore] = useState(false);

  const interactionType = watch("interaction_type") as InteractionType | undefined;
  const showDirectionField = interactionType && interactionType !== "note";

  return (
    <div className="space-y-2">
      <TextInput
        source="text"
        label={false}
        multiline
        helperText={false}
        placeholder={
          interactionType === "call"
            ? "Call notes / transcription..."
            : interactionType === "sms"
              ? "SMS message content..."
              : interactionType === "chat"
                ? "Chat conversation summary..."
                : "Add a note"
        }
        rows={6}
      />

      {!displayMore && (
        <div className="flex justify-end items-center gap-2">
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setDisplayMore(!displayMore);
              setValue("date", getCurrentDate());
            }}
            className="text-sm text-muted-foreground underline hover:no-underline p-0 h-auto cursor-pointer"
          >
            Show options
          </Button>
          <span className="text-sm text-muted-foreground">
            (interaction type, attachments, or change details)
          </span>
        </div>
      )}

      <div
        className={cn(
          "space-y-3 mt-3 overflow-hidden transition-transform ease-in-out duration-300 origin-top",
          !displayMore ? "scale-y-0 max-h-0 h-0" : "scale-y-100",
        )}
      >
        {/* Interaction Type Selection */}
        <div className="grid grid-cols-2 gap-4">
          <SelectInput
            source="interaction_type"
            choices={interactionTypeChoices}
            optionText={interactionTypeRenderer}
            defaultValue="note"
            helperText={false}
            label="Type"
          />
          {showDirectionField && (
            <SelectInput
              source="direction"
              choices={directionChoices}
              defaultValue="outbound"
              helperText={false}
              label="Direction"
            />
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          {showStatus && (
            <SelectInput
              source="status"
              choices={noteStatuses.map((status) => ({
                id: status.value,
                name: status.label,
                value: status.value,
              }))}
              optionText={statusRenderer}
              defaultValue={"warm"}
              helperText={false}
            />
          )}
          <DateTimeInput
            source="date"
            label="Date"
            helperText={false}
            className="text-primary"
            defaultValue={getCurrentDate()}
          />
        </div>

        {/* Call-specific fields */}
        {interactionType === "call" && (
          <div className="grid grid-cols-2 gap-4">
            <TextInput
              source="recording_url"
              label="Recording URL"
              helperText={false}
              placeholder="https://..."
            />
            <TextInput
              source="duration_seconds"
              label="Duration (seconds)"
              helperText={false}
              type="number"
            />
          </div>
        )}

        <FileInput source="attachments" multiple>
          <FileField source="src" title="title" target="_blank" />
        </FileInput>
      </div>
    </div>
  );
};

const statusRenderer = (choice: any) => {
  return (
    <div>
      <Status status={choice.value} /> {choice.name}
    </div>
  );
};

const interactionTypeRenderer = (choice: any) => {
  return (
    <div className="flex items-center gap-2">
      <InteractionIcon type={choice.value as InteractionType} size="sm" />
      <span>{choice.name}</span>
    </div>
  );
};
